/**
 * SAB-788 — draft-time capture and display.
 *
 * THE BUG THIS EXISTS TO PREVENT
 * `<input type="datetime-local">` yields a NAIVE local string with no offset —
 * `"2026-08-31T19:15"`. nfl-create-league passed it straight to a `timestamptz` column, and
 * Postgres (session TimeZone = UTC on Supabase) interpreted it AS UTC. So a commissioner
 * picking 7:15 PM got an instant of 19:15Z — 3:15 PM their time in Eastern, 2:15 PM in
 * Central. Every draft time ever set was wrong by the commissioner's UTC offset, in five of
 * five leagues, until 2026-08-24.
 *
 * Reproduced exactly at the time:
 *   select '2026-08-31T19:15'::timestamptz;   -- 2026-08-31 19:15:00+00, byte-identical
 *                                             -- to the value stored for League of Trash
 *
 * THE APPROACH
 * Browser-offset capture, matching ESPN / Yahoo / Sleeper: the commissioner picks a
 * wall-clock time in their own zone, it is converted to an absolute instant HERE, and every
 * member sees it rendered in theirs. No timezone selector — ESPN's mobile app does not even
 * offer a manual override.
 *
 * Both halves live here on purpose. The capture bug was possible because conversion was
 * implicit; the display half is what makes an instant legible to someone in another zone.
 * SAB-789's reschedule control must import these rather than reimplement them — a second
 * copy of the conversion is how this bug gets a second home.
 */

/**
 * Convert a `datetime-local` input value to an absolute instant (ISO 8601 with offset).
 *
 * `new Date("2026-09-01T19:15")` parses a naive string in the BROWSER'S local zone — which
 * is exactly what we want, and exactly what the server-side path got wrong by letting
 * Postgres parse it in UTC instead. `.toISOString()` then yields the unambiguous instant.
 *
 *   commissioner in America/Chicago picks 2026-09-01T19:15
 *     -> "2026-09-02T00:15:00.000Z"   (CDT is UTC-5, so the DATE crosses)
 *
 * The date crossing is not an edge case — any evening draft west of UTC lands on the
 * following UTC day, and a conversion that looks right for the time while dropping the date
 * would be worse than the original bug.
 *
 * Returns null for empty/invalid input so callers can omit the field rather than send junk.
 */
export function localInputToInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Inverse of the above: an instant back into a `datetime-local` input value, in the
 * viewer's zone. Needed by any edit form that pre-fills an existing draft time (SAB-789).
 *
 * Deliberately NOT `toISOString().slice(0,16)` — that would render the UTC wall-clock into
 * a local-interpreted input and reintroduce the offset error on the next save, which is the
 * same class of mistake pointed the other way.
 */
export function instantToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
         `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Render a draft instant WITH its zone abbreviation, in the viewer's zone.
 *
 * The zone is not decoration. A correct instant still renders as a different wall-clock
 * time for every member in a different zone — a commissioner announces "7:15" and a member
 * two zones over sees "5:15" and reasonably assumes something is broken. Showing "5:15 PM
 * CDT" vs "5:15 PM PDT" is what makes the two readings reconcilable.
 *
 *   -> "Tue, Sep 1, 7:15 PM CDT"
 */
export function formatDraftTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short',
  });
}
